import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "@/components/ui/Dialog";
import { ApiFieldErrors } from "@/components/ui/ApiFieldErrors";
import { apiFieldDescribedBy, apiFieldHasError } from "@/components/ui/api-field-errors";
import {
  ApiError,
  DEPLOYMENT_KEY_IN_USE_CODE,
  DEPLOYMENT_NAME_IN_USE_CODE,
} from "@/lib/api/client";
import { listDeployments } from "../api/deployments.api";
import {
  DEPLOYMENT_KEY_MAX_LENGTH,
  DEPLOYMENT_KEY_MIN_LENGTH,
  type DeploymentKeyIssue,
  validateDeploymentKey,
} from "../lib/deployment-key";
import { useCreateDeployment } from "../hooks/useDeployments";

interface CreateDeploymentDialogProps {
  appName: string;
  open: boolean;
  onClose: () => void;
}

export function CreateDeploymentDialog({ appName, open, onClose }: CreateDeploymentDialogProps) {
  const { t } = useTranslation();
  const createDeployment = useCreateDeployment(appName);
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [keyIssue, setKeyIssue] = useState<DeploymentKeyIssue | null>(null);
  const [keyConflict, setKeyConflict] = useState(false);
  const [checkingKeyConflict, setCheckingKeyConflict] = useState(false);
  const conflictCheckId = useRef(0);
  const fieldErrorPrefix = useId();
  const busy = createDeployment.isPending || checkingKeyConflict;
  const showServerFieldErrors = !keyConflict && !checkingKeyConflict;
  const serverError = showServerFieldErrors ? createDeployment.error : null;
  const nameInvalid = apiFieldHasError(serverError, "name");
  const serverKeyInvalid = apiFieldHasError(serverError, "key");

  const keyError = keyIssue
    ? t(`deployments.deploymentKeyErrors.${keyIssue}`, {
        min: DEPLOYMENT_KEY_MIN_LENGTH,
        max: DEPLOYMENT_KEY_MAX_LENGTH,
      })
    : keyConflict
      ? t("deployments.deploymentKeyErrors.conflict")
      : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (createDeployment.isPending) return;
    if (checkingKeyConflict) return;
    if (!name.trim()) return;

    const normalizedName = name.trim();
    const normalizedKey = key.trim();
    conflictCheckId.current += 1;
    setCheckingKeyConflict(false);
    const issue = validateDeploymentKey(normalizedKey);
    setKeyIssue(issue);
    setKeyConflict(false);
    if (issue) {
      if (createDeployment.error) createDeployment.reset();
      return;
    }

    try {
      await createDeployment.mutateAsync({
        name: normalizedName,
        key: normalizedKey || undefined,
      });
      setName("");
      setKey("");
      setKeyIssue(null);
      onClose();
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 409) return;
      if (error.code === DEPLOYMENT_KEY_IN_USE_CODE) {
        setKeyConflict(true);
        return;
      }
      if (error.code === DEPLOYMENT_NAME_IN_USE_CODE || !normalizedKey) return;

      const checkId = ++conflictCheckId.current;
      setCheckingKeyConflict(true);
      try {
        const result = await listDeployments(appName);
        if (conflictCheckId.current === checkId) {
          setKeyConflict(
            !result.deployments.some((deployment) => deployment.name === normalizedName),
          );
        }
      } catch {
        if (conflictCheckId.current === checkId) setKeyConflict(false);
      } finally {
        if (conflictCheckId.current === checkId) setCheckingKeyConflict(false);
      }
    }
  };

  const handleKeyChange = (value: string) => {
    conflictCheckId.current += 1;
    setKey(value);
    setKeyIssue(null);
    setKeyConflict(false);
    setCheckingKeyConflict(false);
    if (createDeployment.error) createDeployment.reset();
  };

  const handleNameChange = (value: string) => {
    conflictCheckId.current += 1;
    setName(value);
    setKeyConflict(false);
    setCheckingKeyConflict(false);
    if (createDeployment.error) createDeployment.reset();
  };

  const handleClose = () => {
    if (!busy) {
      conflictCheckId.current += 1;
      setName("");
      setKey("");
      setKeyIssue(null);
      setKeyConflict(false);
      setCheckingKeyConflict(false);
      createDeployment.reset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} title={t("deployments.createDeployment")}>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="deployment-name" className="text-sm font-medium text-subtle">
            {t("deployments.deploymentNameLabel")}
          </label>
          <input
            id="deployment-name"
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder={t("deployments.deploymentNamePlaceholder")}
            data-autofocus
            readOnly={createDeployment.isPending}
            disabled={checkingKeyConflict && !createDeployment.isPending}
            aria-disabled={busy || undefined}
            aria-invalid={nameInvalid ? true : undefined}
            aria-describedby={apiFieldDescribedBy(serverError, "name", fieldErrorPrefix)}
            className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 hover:border-border-light"
          />
        </div>

        <div className="mt-4 flex flex-col gap-1.5">
          <label htmlFor="deployment-key" className="text-sm font-medium text-subtle">
            {t("deployments.deploymentKeyLabel")}
          </label>
          <input
            id="deployment-key"
            type="text"
            value={key}
            onChange={(e) => handleKeyChange(e.target.value)}
            placeholder={t("deployments.deploymentKeyPlaceholder")}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            readOnly={createDeployment.isPending}
            disabled={checkingKeyConflict && !createDeployment.isPending}
            aria-disabled={busy || undefined}
            aria-invalid={keyError || serverKeyInvalid ? true : undefined}
            aria-describedby={
              keyError
                ? "deployment-key-error"
                : apiFieldDescribedBy(serverError, "key", fieldErrorPrefix, "deployment-key-hint")
            }
            className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 hover:border-border-light"
          />
          {keyError ? (
            <p id="deployment-key-error" role="alert" className="text-xs text-danger">
              {keyError}
            </p>
          ) : (
            <p id="deployment-key-hint" className="text-xs text-muted">
              {t("deployments.deploymentKeyHint")}
            </p>
          )}
        </div>

        {showServerFieldErrors && (
          <ApiFieldErrors
            error={createDeployment.error}
            fieldLabels={{
              name: t("deployments.deploymentNameLabel"),
              key: t("deployments.deploymentKeyLabel"),
            }}
            idPrefix={fieldErrorPrefix}
            className="mt-3"
          />
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            aria-disabled={createDeployment.isPending || undefined}
            disabled={checkingKeyConflict}
            className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading disabled:opacity-50 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={!createDeployment.isPending && (!name.trim() || checkingKeyConflict)}
            aria-disabled={createDeployment.isPending || undefined}
            aria-busy={createDeployment.isPending || undefined}
            data-autofocus={createDeployment.isPending ? true : undefined}
            className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {busy ? t("deployments.creating") : t("deployments.createDeployment")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
